import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiClient from "../services/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Briefcase,
  Users,
  DollarSign,
  CheckCircle,
  Clock,
  ArrowRight,
  FileText,
  Receipt,
  Building2,
  Calendar,
  AlertCircle,
  Gavel,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { formatCurrency, formatDate } from "../lib/formatters";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { MissingReportsAlert, NotificationPrompt } from "../components/MissingReportsAlert";
import { BackupAlert } from "../components/BackupAlert";
import { useConfirm } from "../components/ui/confirm-dialog";
const DashboardPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const canManageJobs = user?.role === "admin" || user?.role === "project_manager";
  const canSkipBilling = ["admin", "project_manager", "office_manager"].includes(user?.role);

  const skipBillingMonth = async (e, billing) => {
    e.preventDefault();
    e.stopPropagation();
    const { job_id: jobId, month, job_name: jobName } = billing;
    const monthLabel = new Date(`${month}-15T00:00:00`).toLocaleDateString("en-US", { month: "long", year: "numeric" });
    const yes = await confirm({
      title: "No billing this month?",
      message: `Hide ${jobName} from Billings Due for ${monthLabel}?\nIt will come back automatically next month.`,
      confirmText: "Yes, no billing",
    });
    if (!yes) return;
    setStats((prev) => ({ ...prev, billings_due: prev.billings_due.filter((b) => b.job_id !== jobId) }));
    try {
      await apiClient.post(`/jobs/${jobId}/billing-skips`, { month });
      toast.success(`${jobName}: no billing this month`, {
        action: {
          label: "Undo",
          onClick: async () => {
            await apiClient.delete(`/jobs/${jobId}/billing-skips/${month}`);
            setStats((prev) => ({ ...prev, billings_due: [...prev.billings_due, billing] }));
          },
        },
      });
    } catch {
      setStats((prev) => ({ ...prev, billings_due: [...prev.billings_due, billing] }));
      toast.error("Could not update billing reminder");
    }
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const statsRes = await apiClient.get(`/dashboard/stats`);
        setStats(statsRes.data);
      } catch (error) {
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [getAuthHeaders]);

  const getStatusBadge = (status) => {
    const variants = {
      bidding: "badge-bidding",
      precon: "badge-precon",
      active: "badge-active",
      complete: "badge-complete",
      archive: "badge-archive",
      not_bid: "badge-archive",
      bid_complete: "badge-bid-complete",
      not_won: "badge-archive",
    };
    const labels = {
      bidding: "Bidding",
      precon: "Precon",
      active: "Active",
      complete: "Complete",
      archive: "Archive",
      not_bid: "Not Bid",
      bid_complete: "Bid Complete",
      not_won: "Not Won",
    };
    return (
      <span className={`badge-status ${variants[status] || "badge-precon"}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="page-enter space-y-8" data-testid="dashboard-page">
      {/* Page Header */}
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-foreground">
            Dashboard
          </h1>
        </div>
      </div>

      {/* Missing Reports Alert */}
      <MissingReportsAlert />

      {/* Backup Reminder Alert */}
      <BackupAlert userRole={user?.role} getAuthHeaders={getAuthHeaders} />

      {/* Notification Permission Prompt */}
      <NotificationPrompt />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card 
          className="stat-card cursor-pointer hover:shadow-md transition-shadow" 
          data-testid="stat-active-jobs"
          onClick={() => navigate("/jobs?status=active")}
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="stat-label">Active Jobs</p>
              <p className="stat-value mt-2">{stats?.active_jobs || 0}</p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-sm flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card 
          className="stat-card cursor-pointer hover:shadow-md transition-shadow" 
          data-testid="stat-completed-jobs"
          onClick={() => navigate("/jobs?status=complete")}
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="stat-label">Completed</p>
              <p className="stat-value mt-2">{stats?.completed_jobs || 0}</p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-sm flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </Card>

        <Card 
          className="stat-card cursor-pointer hover:shadow-md transition-shadow" 
          data-testid="stat-employees"
          onClick={() => navigate("/employees")}
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="stat-label">Employees</p>
              <p className="stat-value mt-2">{stats?.total_employees || 0}</p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-sm flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </Card>

        <Card 
          className="stat-card cursor-pointer hover:shadow-md transition-shadow" 
          data-testid="stat-bids-outstanding"
          onClick={() => navigate("/jobs?status=bid_complete")}
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="stat-label">Bids Outstanding</p>
              <p className="stat-value mt-2 text-amber-600">
                {formatCurrency(stats?.bids_outstanding || 0)}
              </p>
            </div>
            <div className="w-10 h-10 bg-amber-100 rounded-sm flex items-center justify-center">
              <Gavel className="w-5 h-5 text-amber-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Financial Summary & Recent Jobs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="financial-summary">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              Financial Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-border">
              <span className="text-muted-foreground">Total Estimates</span>
              <span className="font-semibold text-foreground">
                {formatCurrency(stats?.total_estimates)}
              </span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-border">
              <span className="text-muted-foreground">Total Actual Costs</span>
              <span className="font-semibold text-foreground">
                {formatCurrency(stats?.total_actuals)}
              </span>
            </div>
            <div className="flex justify-between items-center py-3">
              <span className="text-muted-foreground font-medium">
                Net Profit/Loss
              </span>
              <span
                className={`font-bold text-lg ${
                  (stats?.total_profit_loss || 0) >= 0
                    ? "profit-positive"
                    : "profit-negative"
                }`}
              >
                {formatCurrency(stats?.total_profit_loss)}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Billings Due */}
        <Card data-testid="billings-due">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Billings Due This Month
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!stats?.billings_due || stats.billings_due.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-muted-foreground">Nothing left to bill this month</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1" data-testid="billings-due-list">
                {stats.billings_due.map((billing) => {
                  const days = billing.days_until_due;
                  const noDate = days === null || days === undefined;
                  const isRed = !noDate && days <= 2;
                  const isYellow = !noDate && days >= 3 && days <= 5;
                  const bgClass = noDate
                    ? "bg-slate-50 border border-slate-200"
                    : isRed
                      ? "bg-red-50 border border-red-200"
                      : isYellow
                        ? "bg-amber-50 border border-amber-200"
                        : "bg-green-50 border border-green-200";
                  const textClass = noDate
                    ? "text-slate-600"
                    : isRed
                      ? "text-red-700"
                      : isYellow
                        ? "text-amber-700"
                        : "text-green-700";
                  const skipBtnClass = noDate
                    ? "bg-slate-600 hover:bg-slate-700"
                    : isRed
                      ? "bg-red-700 hover:bg-red-800"
                      : isYellow
                        ? "bg-amber-700 hover:bg-amber-800"
                        : "bg-green-700 hover:bg-green-800";
                  const statusText = noDate
                    ? "No billing day set"
                    : days < 0
                      ? `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} overdue`
                      : days === 0
                        ? "Due Today"
                        : days === 1
                          ? "Tomorrow"
                          : `In ${days} days`;
                  return (
                  <Link
                    key={billing.job_id}
                    to={`/jobs/${billing.job_id}?tab=billings`}
                    className={`flex items-center justify-between p-3 rounded-sm hover:opacity-80 transition-colors ${bgClass}`}
                    data-testid={`billing-due-${billing.job_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {isRed && (
                          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                        )}
                        <p className="font-medium text-foreground truncate">
                          {billing.job_name}
                        </p>
                        {billing.status && billing.status !== "active" && getStatusBadge(billing.status)}
                      </div>
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="text-sm text-muted-foreground truncate">
                          {billing.client_name}
                        </p>
                        {canSkipBilling && (
                          <button
                            type="button"
                            onClick={(e) => skipBillingMonth(e, billing)}
                            className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium text-white shadow-sm transition-colors ${skipBtnClass}`}
                            title="Hide this job from Billings Due for the current month only"
                            data-testid={`billing-skip-${billing.job_id}`}
                          >
                            No billing this month
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 text-right">
                      <p className={`text-sm font-medium ${textClass}`}>{statusText}</p>
                      {billing.billing_date && (
                        <p className={`text-xs font-medium ${textClass}`}>
                          {formatDate(billing.billing_date)}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground" data-testid={`billing-due-remaining-${billing.job_id}`}>
                        {formatCurrency(billing.remaining_to_bill)} left to bill
                      </p>
                    </div>
                  </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bids Due */}
      <Card data-testid="bids-due-soon">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gavel className="w-5 h-5 text-amber-600" />
            Bids Due
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!stats?.bids_due_soon || stats.bids_due_soon.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-muted-foreground">No bids due and no pending submissions</p>
            </div>
          ) : (
            <div className="space-y-3">
              {stats.bids_due_soon.map((bid) => {
                const isBidComplete = bid.status === "bid_complete";
                const bizDays = bid.business_days_until_due ?? bid.days_until_due;
                const calDays = bid.days_until_due;
                const isOverdue = calDays < 0;
                const isOrange = !isOverdue && bizDays >= 0 && bizDays <= 3;
                const isYellow = !isOverdue && bizDays >= 4 && bizDays <= 6;
                const isBlue = !isOverdue && bizDays >= 7 && bizDays <= 15;
                const isGray = !isOverdue && bizDays >= 16;

                // Background + border color based on urgency
                const rowBg = isOverdue
                  ? "bg-red-50 border border-red-200"
                  : isOrange
                    ? "bg-orange-50 border border-orange-200"
                    : isYellow
                      ? "bg-yellow-50 border border-yellow-200"
                      : isBlue
                        ? "bg-blue-50 border border-blue-200"
                        : "bg-gray-50 border border-gray-200";

                // Center date color: darker shade of background
                const dateCenterColor = isOverdue
                  ? "text-red-400/80"
                  : isOrange
                    ? "text-orange-400/80"
                    : isYellow
                      ? "text-yellow-500/80"
                      : isBlue
                        ? "text-blue-400/80"
                        : "text-gray-400/80";

                // Right-side status text color
                const statusColor = isOverdue
                  ? "text-red-600"
                  : isOrange
                    ? "text-orange-600"
                    : isYellow
                      ? "text-yellow-600"
                      : isBlue
                        ? "text-blue-600"
                        : "text-gray-500";

                const statusText = isBidComplete
                  ? "Mark as Sent"
                  : isOverdue
                    ? "Overdue"
                    : calDays === 0
                      ? "Due Today"
                      : calDays === 1
                        ? "Tomorrow"
                        : `In ${bizDays} days`;

                return (
                <Link
                  key={bid.job_id}
                  to={`/jobs/${bid.job_id}?tab=notes`}
                  className={`relative flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 rounded-md overflow-hidden hover:opacity-90 transition-all ${rowBg}`}
                  data-testid={`bid-due-${bid.job_id}`}
                >
                  {/* Center date watermark - desktop only */}
                  {bid.bid_due_date && (
                    <span className={`hidden sm:flex absolute top-3 left-[35%] right-[12%] items-start justify-center gap-2 pointer-events-none select-none ${dateCenterColor}`}>
                      <span className="text-3xl font-bold">{formatDate(bid.bid_due_date)}</span>
                      {bid.bid_due_time && (
                        <span className="text-lg font-semibold mt-1.5">
                          {new Date(`2000-01-01T${bid.bid_due_time}`).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}
                        </span>
                      )}
                    </span>
                  )}

                  {/* Top row: job name + badge */}
                  <div className="min-w-0 flex-1 relative z-10">
                    <div className="flex items-center gap-2">
                      {isOverdue && (
                        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                      )}
                      {isOrange && (
                        <AlertCircle className="w-4 h-4 text-orange-600 flex-shrink-0" />
                      )}
                      <p className="text-base sm:text-lg font-semibold text-foreground truncate">
                        {bid.job_name}
                      </p>
                      {isBidComplete && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium whitespace-nowrap">
                          Pending Submissions
                        </span>
                      )}
                    </div>
                    <p className="text-sm sm:text-base text-muted-foreground truncate">
                      {bid.client_name}
                    </p>
                  </div>

                  {/* Mobile: date/time + status + price row */}
                  <div className="flex sm:hidden items-center justify-between mt-2 relative z-10">
                    <div className="flex items-center gap-2 text-sm">
                      {bid.bid_due_date && (
                        <span className={`font-semibold ${statusColor}`}>
                          {formatDate(bid.bid_due_date)}
                        </span>
                      )}
                      {bid.bid_due_time && (
                        <span className={`text-xs ${statusColor}`}>
                          {new Date(`2000-01-01T${bid.bid_due_time}`).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}
                        </span>
                      )}
                      <span className={`text-xs font-medium ${statusColor}`}>
                        {statusText}
                      </span>
                    </div>
                    {bid.bid_total > 0 && (
                      <p className="text-base font-bold text-primary">
                        {formatCurrency(bid.bid_total)}
                      </p>
                    )}
                  </div>

                  {/* Desktop: right side status + price */}
                  <div className="hidden sm:block ml-4 text-right relative z-10">
                    <p className={`text-sm font-medium ${statusColor}`}>
                      {statusText}
                    </p>
                    {bid.bid_total > 0 && (
                      <p className="text-xl font-bold text-primary">
                        {formatCurrency(bid.bid_total)}
                      </p>
                    )}
                  </div>
                </Link>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contracts Outstanding */}
      <Card data-testid="contracts-outstanding">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Contracts Outstanding
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Total at the top */}
          <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex justify-between items-center">
              <span className="font-medium text-blue-700">Total Remaining to Bill</span>
              <span className="text-2xl font-bold text-blue-800">
                {formatCurrency(stats?.total_contracts_outstanding || 0)}
              </span>
            </div>
          </div>
          
          {/* Job list */}
          {stats?.contracts_outstanding?.length > 0 ? (
            <div className="space-y-2">
              {stats.contracts_outstanding.map((item) => (
                <Link
                  key={item.job_id}
                  to={`/jobs/${item.job_id}`}
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                  data-testid={`contract-outstanding-${item.job_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground truncate">{item.job_name}</p>
                    <p className="text-xs text-muted-foreground">
                      Contract: {formatCurrency(item.total_contract)} | Billed: {formatCurrency(item.total_billed)}
                    </p>
                  </div>
                  <div className="ml-4 text-right">
                    <p className="font-semibold text-blue-600">
                      {formatCurrency(item.remaining_to_bill)}
                    </p>
                    <p className="text-xs text-muted-foreground">remaining</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-4">
              No outstanding contracts to bill
            </p>
          )}
        </CardContent>
      </Card>

      {/* Billings Outstanding */}
      <Card data-testid="billings-outstanding">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Billings Outstanding
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.billings_summary?.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Job</TableHead>
                    <TableHead className="text-right">Billed</TableHead>
                    <TableHead className="text-right">Received</TableHead>
                    <TableHead className="text-right">Outstanding</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.billings_summary.map((item) => (
                    <TableRow key={item.job_id} className="table-row cursor-pointer hover:bg-muted/40 transition-colors" onClick={() => navigate(`/jobs/${item.job_id}`)}>
                      <TableCell className="font-medium">
                        {item.job_name}
                      </TableCell>
                      <TableCell className="text-right font-mono text-yellow-600">
                        {formatCurrency(item.billings_sent)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-green-600">
                        {formatCurrency(item.billings_received)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-red-600">
                        {formatCurrency(item.billings_outstanding)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-muted/50 font-bold">
                    <TableCell colSpan={3}>Total Outstanding</TableCell>
                    <TableCell className="text-right font-mono text-red-700">
                      {formatCurrency(stats?.total_billings_outstanding || 0)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              No outstanding billings
            </p>
          )}
        </CardContent>
      </Card>

      {/* Invoices Outstanding by Job */}
      <Card data-testid="invoices-outstanding">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-primary" />
            Invoices Outstanding by Job
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.invoices_outstanding_by_job?.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Job</TableHead>
                    <TableHead className="text-right">Supplier</TableHead>
                    <TableHead className="text-right">Equipment</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.invoices_outstanding_by_job.map((item) => (
                    <TableRow key={item.job_id} className="table-row cursor-pointer hover:bg-muted/40 transition-colors" onClick={() => navigate(`/jobs/${item.job_id}`)}>
                      <TableCell className="font-medium">
                        {item.job_name}
                      </TableCell>
                      <TableCell className="text-right font-mono text-red-600">
                        {formatCurrency(item.supplier_amount || 0)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-red-600">
                        {formatCurrency(item.equipment_amount || 0)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-red-700 font-medium">
                        {formatCurrency(item.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-red-50 font-bold">
                    <TableCell>Total Outstanding</TableCell>
                    <TableCell className="text-right font-mono text-red-600">
                      {formatCurrency((stats?.total_invoices_outstanding || 0) - (stats?.total_equipment_invoices_outstanding || 0))}
                    </TableCell>
                    <TableCell className="text-right font-mono text-red-600">
                      {formatCurrency(stats?.total_equipment_invoices_outstanding || 0)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-red-700">
                      {formatCurrency(stats?.total_invoices_outstanding || 0)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              No outstanding invoices
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
